/**
 * Shared parsing for the persisted TRF "sent" instant (GEN-5494).
 *
 * The banner on the in-progress order page shows a server-sourced instant. Two
 * paths feed it — the order-details fetch on load and the PUT result after Send
 * Updates — and both must agree on what counts as a usable time. Centralizing
 * that here keeps the load fallback, the monotonic post-save guard, and the
 * banner label from drifting apart.
 *
 * provider-api serializes `timestamptz` as ISO-8601 with an offset. An
 * offset-less date-time (`YYYY-MM-DDThh:mm[:ss[.fff]]`) is pinned to UTC (the
 * server contract) rather than the viewer's local zone. A date-only value (no
 * time) is too ambiguous to display honestly and is treated as unusable.
 */

/**
 * Full ISO-8601 date-time, captured component-by-component: `YYYY-MM-DDThh:mm`,
 * optional `:ss` and fractional seconds, optional `Z` / `±hh:mm` / `±hhmm`
 * offset (sign, hours, minutes captured separately for range validation). Date-
 * only values, garbage, and any string that merely ends in an offset are
 * rejected by the anchored structure; every component is range-checked below.
 */
const ISO_DATETIME_RE =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?(?:(Z)|([+-])(\d{2}):?(\d{2}))?$/i

/**
 * Parse a persisted TRF-sent string to a `Date`, or null when it is absent,
 * empty, a date-only value, or otherwise not a valid ISO-8601 date-time. An
 * offset-less date-time is interpreted as UTC.
 *
 * The instant is built from the validated components with `Date.UTC` /
 * `setUTCFullYear` (so years 0000–0099 are honored, not remapped to 1900) rather
 * than handing the string to `new Date`, whose parsing of `±hhmm` offsets and
 * long fractions is engine-defined. This keeps parsing deterministic across
 * engines and rejects out-of-range date, time, and offset components.
 */
export function parseTrfSentAt(value: string | null | undefined): Date | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const match = ISO_DATETIME_RE.exec(trimmed)
  if (match == null) return null

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const hour = Number(match[4])
  const minute = Number(match[5])
  const second = match[6] == null ? 0 : Number(match[6])
  const fraction = match[7]
  const offsetSign = match[9]
  const offsetHours = match[10]
  const offsetMinutes = match[11]

  if (month < 1 || month > 12) return null
  if (day < 1 || day > 31) return null
  if (hour > 23 || minute > 59 || second > 59) return null

  // Milliseconds from the first three fraction digits (Python emits microseconds).
  const millisecond = fraction ? Number(fraction.slice(0, 3).padEnd(3, '0')) : 0

  // Build the wall-clock instant in UTC. `setUTCFullYear` sidesteps `Date.UTC`'s
  // 0–99 → 1900s remapping and, with the range checks above, only the calendar
  // day can roll over (e.g. Feb 30) — which the round-trip below rejects.
  const instant = new Date(0)
  instant.setUTCFullYear(year, month - 1, day)
  instant.setUTCHours(hour, minute, second, millisecond)
  if (instant.getUTCFullYear() !== year || instant.getUTCMonth() !== month - 1 || instant.getUTCDate() !== day) {
    return null
  }

  // `Z` and offset-less both mean UTC (the server contract). A numeric offset is
  // range-validated, then subtracted to convert the wall time to a true instant.
  if (offsetSign) {
    const offHours = Number(offsetHours)
    const offMinutes = Number(offsetMinutes)
    if (offHours > 23 || offMinutes > 59) return null
    const signedOffsetMs = (offsetSign === '-' ? -1 : 1) * (offHours * 60 + offMinutes) * 60_000
    return new Date(instant.getTime() - signedOffsetMs)
  }
  return instant
}

/**
 * The first candidate that parses to a valid instant, returned as its original
 * string for storage. Used on load to prefer `trf_sent_at` but fall back to
 * `created_at` when the first value is unusable, rather than showing no date.
 */
export function firstParsableTrfSentAt(...candidates: (string | null | undefined)[]): string | null {
  for (const candidate of candidates) {
    if (parseTrfSentAt(candidate) != null) return candidate as string
  }
  return null
}

/**
 * Monotonic merge of the currently shown time with a post-save candidate: the
 * banner may move forward but never backward (a TRF cannot be "un-sent").
 *
 * - An unusable `next` (null, empty, or unparseable) keeps `prev`, so a degraded
 *   PUT response never blanks an already-shown time.
 * - An unusable `prev` takes `next`.
 * - Otherwise `next` wins only when it is not earlier than `prev`.
 */
export function laterTrfSentAt(prev: string | null, next: string | null | undefined): string | null {
  const nextDate = parseTrfSentAt(next)
  if (nextDate == null) return prev
  const prevDate = parseTrfSentAt(prev)
  if (prevDate == null) return next as string
  return nextDate.getTime() >= prevDate.getTime() ? (next as string) : prev
}
